import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Input, InputNumber, Modal, Row, Select, Table, message } from 'antd';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import MonthPicker from 'components/MonthPicker/MonthPicker';
import { createInstallment, deleteInstallment, listInstallments } from '../api/ownerReporting';
import { loadHostProperties, loadHostUnits, loadUnitsForProperty } from '../api/hostListings';
import { useOwnerReportingHost } from '../hooks/useOwnerReportingHost';
import { readPageFilters, toMonthDate, useSyncPageFilters } from './pageFilters';
import { filterInstallmentsByProperty, installmentUnitOption, unitMetaFromHostUnits, unitsForProperty } from './installmentsTable';
import { unitHasOwner } from './servicePackages/formShared';
import { downloadCsvFile } from './revenueCsv';
import { formatInstallmentDuration, mapInstallmentsToCsvRows } from './installmentsCsv';
import tableStyles from './tableOverflow.module.css';

const buildDuration = (startMoment, endMoment) => {
  const start = (startMoment || moment()).clone().startOf('month');
  const end = (endMoment || start).clone().startOf('month');
  if (end.isBefore(start)) return [{ month: start.month() + 1, year: start.year() }];
  const duration = [];
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end, 'month')) {
    duration.push({ month: cursor.month() + 1, year: cursor.year() });
    cursor.add(1, 'month');
  }
  return duration;
};

const emptyForm = propertyId => ({
  label: '',
  amount: undefined,
  unitId: undefined,
  propertyId: propertyId || undefined,
  startMonth: moment(),
  endMonth: moment(),
});

const InstallmentsPage = ({ location, history }) => {
  const { hostId } = useOwnerReportingHost();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [units, setUnits] = useState([]);
  const [hostUnits, setHostUnits] = useState([]);
  const [propertyUnits, setPropertyUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitNameById, setUnitNameById] = useState({});
  const [propertyIdByUnitId, setPropertyIdByUnitId] = useState({});
  const [propertyId, setPropertyId] = useState(() => readPageFilters(location && location.search).propertyId);
  const [values, setValues] = useState(() => emptyForm(readPageFilters(location && location.search).propertyId));

  const load = async () => {
    if (!hostId) return;
    setLoading(true);
    setPropertiesLoading(true);
    try {
      const [inst, props, hostUnits] = await Promise.all([
        listInstallments(hostId),
        loadHostProperties(hostId),
        loadHostUnits(hostId),
      ]);
      setRows(inst || []);
      const propsList = props || [];
      setProperties(propsList);
      const meta = unitMetaFromHostUnits(hostUnits);
      setHostUnits(hostUnits || []);
      setUnitNameById(meta.unitNameById);
      setPropertyIdByUnitId(meta.propertyIdByUnitId);
      setPropertyId(prev => {
        if (prev && propsList.some(p => String(p._id) === String(prev))) return prev;
        return propsList[0] ? String(propsList[0]._id) : undefined;
      });
    } catch (e) {
      message.error('Failed to load instalments');
    } finally {
      setLoading(false);
      setPropertiesLoading(false);
    }
  };

  useEffect(() => {
    if (!hostId) {
      setRows([]);
      setProperties([]);
      setHostUnits([]);
      setPropertyUnits([]);
      setUnitNameById({});
      setPropertyIdByUnitId({});
      setPropertyId(undefined);
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostId]);

  useEffect(() => {
    if (!propertyId) {
      setPropertyUnits([]);
      return undefined;
    }
    let cancelled = false;
    loadUnitsForProperty(propertyId).then(list => {
      if (cancelled) return;
      const unitList = list || [];
      setPropertyUnits(unitList);
      setUnitNameById(prev => {
        const next = Object.assign({}, prev);
        unitList.forEach(u => {
          next[String(u._id)] = u.name || String(u._id);
        });
        return next;
      });
      setPropertyIdByUnitId(prev => {
        const next = Object.assign({}, prev);
        unitList.forEach(u => {
          next[String(u._id)] = String(propertyId);
        });
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  useEffect(() => {
    if (!values.propertyId) {
      setUnits([]);
      setUnitsLoading(false);
      return;
    }
    let cancelled = false;
    setUnitsLoading(true);

    const fromHost = unitsForProperty(hostUnits, values.propertyId);
    if (fromHost.length) {
      setUnits(fromHost);
      setUnitsLoading(false);
      return;
    }

    loadUnitsForProperty(values.propertyId)
      .then(list => {
        if (cancelled) return;
        const unitList = list || [];
        setUnits(unitList);
        setUnitNameById(prev => {
          const next = Object.assign({}, prev);
          unitList.forEach(u => {
            next[String(u._id)] = u.name || String(u._id);
          });
          return next;
        });
      })
      .catch(() => {
        if (!cancelled) setUnits([]);
      })
      .finally(() => {
        if (!cancelled) setUnitsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [values.propertyId, hostUnits]);

  useSyncPageFilters(history, location, {
    property: propertyId,
  });

  const filteredRows = useMemo(
    () => filterInstallmentsByProperty(rows, propertyId, propertyIdByUnitId),
    [rows, propertyId, propertyIdByUnitId]
  );

  const selectedPropertyName = useMemo(() => {
    const found = (properties || []).find(p => String(p._id) === String(propertyId));
    return (found && found.name) || '';
  }, [properties, propertyId]);

  const csvUnits = useMemo(() => (hostUnits || []).concat(propertyUnits || []), [hostUnits, propertyUnits]);

  const installmentsCsvData = useMemo(
    () =>
      mapInstallmentsToCsvRows({
        rows: filteredRows,
        units: csvUnits,
        propertyName: selectedPropertyName,
      }),
    [filteredRows, csvUnits, selectedPropertyName]
  );

  const installmentsCsvFilename = `${selectedPropertyName}__Instalments.csv`;

  const set = key => val => setValues(prev => ({ ...prev, [key]: val }));

  const onSubmit = async () => {
    if (!values.label || !values.unitId) {
      message.error('Label and unit are required');
      return;
    }
    const selectedUnit = (units || []).find(u => String(u._id) === String(values.unitId));
    if (!unitHasOwner(selectedUnit)) {
      message.error('Select a unit that has an owner');
      return;
    }
    if (values.amount == null || values.amount === '') {
      message.error('Monthly Payment is required');
      return;
    }
    try {
      await createInstallment(hostId, {
        label: values.label,
        amount: Number(values.amount) || 0,
        unitId: values.unitId,
        startMonth: values.startMonth.month() + 1,
        startYear: values.startMonth.year(),
        endMonth: values.endMonth.month() + 1,
        endYear: values.endMonth.year(),
        duration: buildDuration(values.startMonth, values.endMonth),
      });
      message.success('Created');
      setVisible(false);
      load();
    } catch (e) {
      message.error('Create failed');
    }
  };

  return (
    <Card
      title="Owner Reporting — Instalments"
      extra={
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Button
            icon="download"
            disabled={!installmentsCsvData.length}
            onClick={() => downloadCsvFile(installmentsCsvFilename, installmentsCsvData)}
          >
            Download CSV
          </Button>
          <Button
            type="primary"
            disabled={!hostId}
            onClick={() => {
              setValues(emptyForm(propertyId));
              setVisible(true);
            }}
          >
            New Instalment
          </Button>
        </div>
      }
    >
      <Form layout="vertical" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 12]} type="flex" align="bottom">
          <Col xs={24} sm={12} md={10} lg={8}>
            <Form.Item label="Property" style={{ marginBottom: 0 }}>
              <Select
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
                placeholder={propertiesLoading ? 'Loading properties...' : 'Select property'}
                value={propertyId}
                onChange={val => setPropertyId(val ? String(val) : undefined)}
                disabled={!hostId}
                loading={propertiesLoading}
              >
                {properties.map(p => (
                  <Select.Option key={String(p._id)} value={String(p._id)}>
                    {p.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <div className={tableStyles.tableWrap}>
        <Table
          rowKey="_id"
          loading={loading}
          dataSource={filteredRows}
          scroll={{ x: 960 }}
          columns={[
            { title: 'Label', dataIndex: 'label', width: 200 },
            { title: 'Monthly Payment', dataIndex: 'amount', width: 150 },
            {
              title: 'Unit',
              width: 240,
              render: r => {
                const name = unitNameById[String(r.unitId)] || String(r.unitId);
                return <span title={name}>{name}</span>;
              },
            },
            {
              title: 'Duration',
              width: 260,
              className: tableStyles.ellipsisCell,
              render: r => {
                const text = formatInstallmentDuration(r.duration);
                return (
                  <span className={tableStyles.ellipsis} title={text}>
                    {text}
                  </span>
                );
              },
            },
            {
              title: 'Actions',
              width: 90,
              render: r => (
                <Button
                  type="link"
                  onClick={async () => {
                    await deleteInstallment(hostId, r._id);
                    message.success('Deleted');
                    load();
                  }}
                >
                  Delete
                </Button>
              ),
            },
          ]}
        />
      </div>
      <Modal visible={visible} title="New Instalment" onOk={onSubmit} onCancel={() => setVisible(false)} destroyOnClose>
        <Form layout="vertical">
          <Form.Item label="Label" required>
            <Input
              value={values.label}
              onChange={e => set('label')(e.target.value)}
              placeholder="e.g. Owner advance / loan repayment"
            />
          </Form.Item>
          <Form.Item label="Monthly Payment" required>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              value={values.amount}
              onChange={set('amount')}
              placeholder="Amount deducted each month"
            />
          </Form.Item>
          <Form.Item label="Property" required>
            <Select
              showSearch
              optionFilterProp="children"
              style={{ width: '100%' }}
              value={values.propertyId}
              onChange={val => setValues(prev => ({ ...prev, propertyId: val, unitId: undefined }))}
              placeholder="Select property for this instalment"
            >
              {properties.map(p => (
                <Select.Option key={p._id} value={p._id}>
                  {p.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Unit" required>
            <Select
              showSearch
              optionFilterProp="children"
              style={{ width: '100%' }}
              value={unitsLoading ? undefined : values.unitId}
              onChange={set('unitId')}
              disabled={!values.propertyId || unitsLoading}
              loading={unitsLoading}
              placeholder={
                unitsLoading
                  ? 'Loading units...'
                  : values.propertyId
                  ? 'Select unit to apply payment'
                  : 'Select a property first'
              }
              notFoundContent={unitsLoading ? 'Loading...' : 'No units for this property'}
            >
              {units.map(u => {
                const opt = installmentUnitOption(u);
                return (
                  <Select.Option key={opt.key} value={opt.value} disabled={opt.disabled}>
                    {opt.label}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item label="Start month" required>
            <MonthPicker defaultValue={values.startMonth} onChange={val => set('startMonth')(toMonthDate(val))} />
          </Form.Item>
          <Form.Item label="End month" required>
            <MonthPicker defaultValue={values.endMonth} onChange={val => set('endMonth')(toMonthDate(val))} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default withRouter(InstallmentsPage);
